import {
  AppShell,
  Burger,
  Button,
  Container,
  Drawer,
  Group,
  Menu,
  Stack,
  Text,
  useMantineTheme,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import {
  IconBrandWhatsapp,
  IconHome,
  IconMessage2,
  IconBook,
  IconPhone,
  IconUsers,
  IconChevronDown,
  IconChevronRight,
  IconMapPin,
  IconTool,
  IconCalculator,
} from "@tabler/icons-react";
import React, { useEffect, useState } from "react";
import { Link, Outlet, useNavigate } from "react-router-dom";
import { buildWhatsAppUrl } from "./Services/contact.service";
import Footer from "./Components/Footer";
import SubjectBuddyLogo from "./Components/Shared/SubjectBuddyLogo";
import { countryCityGroups } from "./data/cityPages";

function Layout() {
  const theme = useMantineTheme();
  const [opened, { toggle, close }] = useDisclosure();
  const [activeCountrySlug, setActiveCountrySlug] = useState(
    countryCityGroups[0]?.countrySlug ?? "",
  );
  const [mobileExpandedCountrySlug, setMobileExpandedCountrySlug] = useState<
    string | null
  >(countryCityGroups[0]?.countrySlug ?? null);
  const navigate = useNavigate();
  const whatsappUrl = buildWhatsAppUrl("Hello, I need an expert!");

  function createNavigationItems({ forDesktop = false }) {
    const navigationMenuItems = [
      {
        title: "Home",
        icon: <IconHome size={18} stroke={1.6} />, // Added icon
        onClick: () => {
          close();
          navigate("/");
        },
      },
      {
        title: "Reviews",
        icon: <IconMessage2 size={18} stroke={1.6} />, // Added icon
        onClick: () => {
          close();
          navigate("/reviews");
        },
      },
      {
        title: "About Us",
        icon: <IconUsers size={18} stroke={1.6} />,
        onClick: () => {
          close();
          navigate("/about-us");
        },
      },
      {
        title: "Blogs",
        icon: <IconBook size={18} stroke={1.6} />, // Added icon
        onClick: () => {
          close();
          navigate("/blogs");
        },
      },
      {
        title: "Contact",
        icon: <IconPhone size={18} stroke={1.6} />, // Added icon
        onClick: () => {
          close();
          window.scrollTo({
            top: document.body.scrollHeight,
            behavior: "smooth",
          });
        },
      },
    ];

    const whatsappMenuItem = {
      title: "WhatsApp us",
      icon: <IconBrandWhatsapp size={18} stroke={1.6} />,
      prominent: true,
      href: whatsappUrl,
    };

    const navFont = {
      fontFamily: "Fira Sans, Segoe UI, Arial, sans-serif",
      fontWeight: 500, // Less bold
      letterSpacing: "-0.2px",
      fontSize: forDesktop ? "1.01rem" : "1.08rem",
    };

    const [homeMenuItem, ...secondaryMenuItems] = navigationMenuItems;
    const activeCountryGroup =
      countryCityGroups.find(
        (countryGroup) => countryGroup.countrySlug === activeCountrySlug,
      ) ?? countryCityGroups[0];

    const cityMenu = (
      <Menu
        transitionProps={{ transition: "pop-top-right" }}
        position="bottom-start"
        withinPortal
        offset={8}
        onOpen={() => {
          if (!activeCountrySlug && countryCityGroups[0]) {
            setActiveCountrySlug(countryCityGroups[0].countrySlug);
          }

          if (
            !forDesktop &&
            !mobileExpandedCountrySlug &&
            countryCityGroups[0]
          ) {
            setMobileExpandedCountrySlug(countryCityGroups[0].countrySlug);
          }
        }}
        styles={{
          dropdown: {
            minWidth: forDesktop ? 560 : 320,
            maxWidth: forDesktop ? 640 : 360,
            maxHeight: forDesktop ? "none" : 500,
            overflow: "hidden",
            padding: 8,
            borderRadius: 14,
            border: `1px solid ${theme.colors.gray[2]}`,
            boxShadow: "0 18px 44px rgba(15, 23, 42, 0.12)",
          },
          item: {
            borderRadius: 10,
            color: theme.colors.dark[7],
            fontFamily: "Fira Sans, Segoe UI, Arial, sans-serif",
            fontWeight: 600,
            fontSize: "0.96rem",
            paddingTop: 10,
            paddingBottom: 10,
          },
        }}
      >
        <Menu.Target>
          <Button
            variant="subtle"
            size={forDesktop ? "md" : "lg"}
            leftSection={<IconMapPin size={18} stroke={1.6} />}
            rightSection={<IconChevronDown size={16} />}
            styles={{
              root: {
                borderRadius: forDesktop ? 18 : 16,
                ...navFont,
                color: theme.colors.dark[7],
                marginLeft: 2,
                marginRight: 2,
                paddingLeft: 12,
                paddingRight: 12,
                minWidth: 0,
                transition: "all 0.2s cubic-bezier(0.4,0,0.2,1)",
                gap: 6,
                justifyContent: forDesktop ? undefined : "flex-start",
              },
            }}
            fullWidth={!forDesktop}
          >
            City
          </Button>
        </Menu.Target>
        <Menu.Dropdown>
          <Menu.Label
            style={{
              color: theme.colors.gray[6],
              fontFamily: "Fira Sans, Segoe UI, Arial, sans-serif",
              fontWeight: 700,
              letterSpacing: 0,
              padding: "8px 10px",
            }}
          >
            Choose country and city
          </Menu.Label>
          {forDesktop ? (
            <div
              style={{
                display: "flex",
                alignItems: "stretch",
                minHeight: 360,
              }}
            >
              <div
                style={{
                  width: 224,
                  maxHeight: 380,
                  overflowY: "auto",
                  padding: "4px 8px 4px 4px",
                }}
              >
                {countryCityGroups.map((countryGroup) => {
                  const isActive =
                    countryGroup.countrySlug ===
                    activeCountryGroup?.countrySlug;

                  return (
                    <button
                      key={countryGroup.countrySlug}
                      type="button"
                      onMouseEnter={() =>
                        setActiveCountrySlug(countryGroup.countrySlug)
                      }
                      onFocus={() =>
                        setActiveCountrySlug(countryGroup.countrySlug)
                      }
                      onClick={() =>
                        setActiveCountrySlug(countryGroup.countrySlug)
                      }
                      style={{
                        width: "100%",
                        border: "none",
                        background: isActive
                          ? theme.colors.green[0]
                          : "transparent",
                        color: theme.colors.dark[7],
                        borderRadius: 12,
                        cursor: "pointer",
                        padding: "12px 14px",
                        marginBottom: 6,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        textAlign: "left",
                        transition: "background-color 160ms ease",
                      }}
                    >
                      <span>
                        <span
                          style={{
                            display: "block",
                            fontFamily:
                              "Fira Sans, Segoe UI, Arial, sans-serif",
                            fontWeight: 700,
                            fontSize: "0.97rem",
                          }}
                        >
                          {countryGroup.country}
                        </span>
                        <span
                          style={{
                            display: "block",
                            marginTop: 2,
                            color: theme.colors.gray[6],
                            fontFamily:
                              "Fira Sans, Segoe UI, Arial, sans-serif",
                            fontSize: "0.82rem",
                            fontWeight: 500,
                          }}
                        >
                          {countryGroup.cities.length} cities
                        </span>
                      </span>
                      <IconChevronRight
                        size={16}
                        stroke={1.8}
                        color={
                          isActive
                            ? theme.colors.green[7]
                            : theme.colors.gray[5]
                        }
                      />
                    </button>
                  );
                })}
              </div>
              <div
                style={{
                  width: 1,
                  background: theme.colors.gray[2],
                }}
              />
              <div
                style={{
                  flex: 1,
                  minWidth: 260,
                  padding: "8px 4px 8px 14px",
                }}
              >
                <Text
                  style={{
                    color: theme.colors.green[7],
                    fontFamily: "Fira Sans, Segoe UI, Arial, sans-serif",
                    fontWeight: 800,
                    fontSize: "1rem",
                    padding: "4px 10px 2px",
                  }}
                >
                  {activeCountryGroup?.country}
                </Text>
                <Text
                  style={{
                    color: theme.colors.gray[6],
                    fontFamily: "Fira Sans, Segoe UI, Arial, sans-serif",
                    fontWeight: 500,
                    fontSize: "0.84rem",
                    padding: "0 10px 10px",
                  }}
                >
                  Choose a city to open its page
                </Text>
                <div
                  style={{
                    maxHeight: 320,
                    overflowY: "auto",
                    paddingRight: 4,
                  }}
                >
                  {activeCountryGroup?.cities.map((city) => (
                    <Menu.Item
                      key={city.path}
                      component={Link}
                      to={city.path}
                      leftSection={<IconMapPin size={16} stroke={1.7} />}
                      onClick={close}
                    >
                      {city.city}
                    </Menu.Item>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <Stack gap={6} p={4}>
              {countryCityGroups.map((countryGroup) => {
                const isExpanded =
                  mobileExpandedCountrySlug === countryGroup.countrySlug;

                return (
                  <div
                    key={countryGroup.countrySlug}
                    style={{
                      border: `1px solid ${
                        isExpanded
                          ? theme.colors.green[2]
                          : theme.colors.gray[2]
                      }`,
                      borderRadius: 12,
                      background: isExpanded
                        ? theme.colors.green[0]
                        : theme.white,
                    }}
                  >
                    <button
                      type="button"
                      onClick={() =>
                        setMobileExpandedCountrySlug((current) =>
                          current === countryGroup.countrySlug
                            ? null
                            : countryGroup.countrySlug,
                        )
                      }
                      style={{
                        width: "100%",
                        border: "none",
                        background: "transparent",
                        cursor: "pointer",
                        padding: "12px 14px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        textAlign: "left",
                      }}
                    >
                      <span>
                        <span
                          style={{
                            display: "block",
                            color: theme.colors.dark[7],
                            fontFamily:
                              "Fira Sans, Segoe UI, Arial, sans-serif",
                            fontWeight: 700,
                            fontSize: "0.98rem",
                          }}
                        >
                          {countryGroup.country}
                        </span>
                        <span
                          style={{
                            display: "block",
                            marginTop: 2,
                            color: theme.colors.gray[6],
                            fontFamily:
                              "Fira Sans, Segoe UI, Arial, sans-serif",
                            fontWeight: 500,
                            fontSize: "0.83rem",
                          }}
                        >
                          {countryGroup.cities.length} cities
                        </span>
                      </span>
                      <IconChevronDown
                        size={16}
                        stroke={1.8}
                        color={
                          isExpanded
                            ? theme.colors.green[7]
                            : theme.colors.gray[6]
                        }
                        style={{
                          transform: isExpanded
                            ? "rotate(180deg)"
                            : "rotate(0deg)",
                          transition: "transform 160ms ease",
                        }}
                      />
                    </button>
                    {isExpanded ? (
                      <div style={{ padding: "0 8px 8px" }}>
                        {countryGroup.cities.map((city) => (
                          <Menu.Item
                            key={city.path}
                            component={Link}
                            to={city.path}
                            leftSection={<IconMapPin size={16} stroke={1.7} />}
                            onClick={close}
                          >
                            {city.city}
                          </Menu.Item>
                        ))}
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </Stack>
          )}
        </Menu.Dropdown>
      </Menu>
    );

    const toolsMenu = (
      <Menu
        transitionProps={{ transition: "pop-top-right" }}
        position="bottom-start"
        withinPortal
        offset={8}
        styles={{
          dropdown: {
            minWidth: 280,
            padding: 8,
            borderRadius: 14,
            border: `1px solid ${theme.colors.gray[2]}`,
            boxShadow: "0 18px 44px rgba(15, 23, 42, 0.12)",
          },
          item: {
            borderRadius: 10,
            color: theme.colors.dark[7],
            fontFamily: "Fira Sans, Segoe UI, Arial, sans-serif",
            fontWeight: 600,
            fontSize: "0.96rem",
            paddingTop: 10,
            paddingBottom: 10,
          },
        }}
      >
        <Menu.Target>
          <Button
            variant="subtle"
            size={forDesktop ? "md" : "lg"}
            leftSection={<IconTool size={18} stroke={1.6} />}
            rightSection={<IconChevronDown size={16} />}
            styles={{
              root: {
                borderRadius: forDesktop ? 18 : 16,
                ...navFont,
                color: theme.colors.dark[7],
                marginLeft: 2,
                marginRight: 2,
                paddingLeft: 12,
                paddingRight: 12,
                minWidth: 0,
                transition: "all 0.2s cubic-bezier(0.4,0,0.2,1)",
                gap: 6,
                justifyContent: forDesktop ? undefined : "flex-start",
              },
            }}
            fullWidth={!forDesktop}
          >
            Tools
          </Button>
        </Menu.Target>
        <Menu.Dropdown>
          <Menu.Label
            style={{
              color: theme.colors.gray[6],
              fontFamily: "Fira Sans, Segoe UI, Arial, sans-serif",
              fontWeight: 700,
              letterSpacing: 0,
              padding: "8px 10px",
            }}
          >
            Study tools
          </Menu.Label>
          <Menu.Item
            component={Link}
            to="/tools"
            leftSection={<IconTool size={16} stroke={1.7} />}
            onClick={close}
          >
            All Student Tools
          </Menu.Item>
          <Menu.Item
            component={Link}
            to="/tools/cursive-generator"
            leftSection={<IconCalculator size={16} stroke={1.7} />}
            onClick={close}
          >
            Cursive Generator
          </Menu.Item>
          <Menu.Item
            component={Link}
            to="/tools/semester-grade-calculator"
            leftSection={<IconCalculator size={16} stroke={1.7} />}
            onClick={close}
          >
            Semester Grade Calculator
          </Menu.Item>
          <Menu.Item
            component={Link}
            to="/tools/grade-calculator"
            leftSection={<IconCalculator size={16} stroke={1.7} />}
            onClick={close}
          >
            Grade Calculator
          </Menu.Item>
          <Menu.Item
            component={Link}
            to="/tools/midterm-grade-calculator"
            leftSection={<IconCalculator size={16} stroke={1.7} />}
            onClick={close}
          >
            Midterm Grade Calculator
          </Menu.Item>
          <Menu.Item
            component={Link}
            to="/tools/final-grade-calculator"
            leftSection={<IconCalculator size={16} stroke={1.7} />}
            onClick={close}
          >
            Final Grade Calculator
          </Menu.Item>
          <Menu.Item
            component={Link}
            to="/tools/letter-grade-calculator"
            leftSection={<IconCalculator size={16} stroke={1.7} />}
            onClick={close}
          >
            Letter Grade Calculator
          </Menu.Item>
          <Menu.Item
            component={Link}
            to="/tools/cumulative-gpa-calculator"
            leftSection={<IconCalculator size={16} stroke={1.7} />}
            onClick={close}
          >
            Cumulative GPA Calculator
          </Menu.Item>
          <Menu.Item
            component={Link}
            to="/tools/ai-college-essay-reviewer"
            leftSection={<IconCalculator size={16} stroke={1.7} />}
            onClick={close}
          >
            AI College Essay Reviewer
          </Menu.Item>
          <Menu.Item
            component={Link}
            to="/tools/college-essay-editor"
            leftSection={<IconCalculator size={16} stroke={1.7} />}
            onClick={close}
          >
            College Essay Editor
          </Menu.Item>
          <Menu.Item
            component={Link}
            to="/tools/speech-word-counter"
            leftSection={<IconCalculator size={16} stroke={1.7} />}
            onClick={close}
          >
            Speech Word Counter
          </Menu.Item>
          <Menu.Item
            component={Link}
            to="/tools/speech-time-calculator"
            leftSection={<IconCalculator size={16} stroke={1.7} />}
            onClick={close}
          >
            Speech Time Calculator
          </Menu.Item>
          <Menu.Item
            component={Link}
            to="/tools/racine-carree-calculator"
            leftSection={<IconCalculator size={16} stroke={1.7} />}
            onClick={close}
          >
            Racine Carrée Calculator
          </Menu.Item>
          <Menu.Item
            component={Link}
            to="/tools/ap-bio-score-calculator"
            leftSection={<IconCalculator size={16} stroke={1.7} />}
            onClick={close}
          >
            AP Bio Score Calculator
          </Menu.Item>
          <Menu.Item
            component={Link}
            to="/tools/apush-score-calculator"
            leftSection={<IconCalculator size={16} stroke={1.7} />}
            onClick={close}
          >
            APUSH Score Calculator
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>
    );

    const renderNavigationButton = ({
      title,
      onClick,
      icon,
    }: typeof homeMenuItem) => (
      <Button
        key={title}
        variant="subtle"
        size={forDesktop ? "md" : "lg"}
        onClick={onClick}
        component="button"
        leftSection={icon}
        styles={{
          root: {
            borderRadius: forDesktop ? 18 : 16,
            ...navFont,
            color: theme.colors.dark[7],
            marginLeft: 2,
            marginRight: 2,
            marginBottom: forDesktop ? undefined : 4,
            paddingLeft: 12,
            paddingRight: 12,
            minWidth: 0,
            transition: "all 0.2s cubic-bezier(0.4,0,0.2,1)",
            gap: forDesktop ? 6 : 8,
          },
        }}
        fullWidth={!forDesktop}
        tabIndex={0}
      >
        {title}
      </Button>
    );

    if (forDesktop) {
      return (
        <>
          {renderNavigationButton(homeMenuItem)}
          {cityMenu}
          {toolsMenu}
          {secondaryMenuItems.map(renderNavigationButton)}
          <Button
            variant="filled"
            size="md"
            component="a"
            href={whatsappMenuItem.href}
            target="_blank"
            rel="noreferrer"
            leftSection={whatsappMenuItem.icon}
            styles={{
              root: {
                borderRadius: 18,
                ...navFont,
                background: theme.colors.green[6],
                color: theme.white,
                boxShadow: `0 2px 8px 0 ${theme.colors.green[1]}`,
                marginLeft: 2,
                marginRight: 2,
                paddingLeft: 12,
                paddingRight: 12,
                minWidth: 0,
                transition: "all 0.2s cubic-bezier(0.4,0,0.2,1)",
                gap: 6,
              },
            }}
            tabIndex={0}
          >
            {whatsappMenuItem.title}
          </Button>
        </>
      );
    } else {
      return (
        <Stack gap={4} style={{ padding: 18 }}>
          {renderNavigationButton(homeMenuItem)}
          {cityMenu}
          {toolsMenu}
          {secondaryMenuItems.map(renderNavigationButton)}
          <Button
            variant="filled"
            size="lg"
            component="a"
            href={whatsappMenuItem.href}
            target="_blank"
            rel="noreferrer"
            leftSection={whatsappMenuItem.icon}
            styles={{
              root: {
                borderRadius: 16,
                ...navFont,
                background: theme.colors.green[6],
                color: theme.white,
                boxShadow: `0 2px 8px 0 ${theme.colors.green[1]}`,
                marginTop: 8,
                minWidth: 0,
                transition: "all 0.2s cubic-bezier(0.4,0,0.2,1)",
                gap: 8,
              },
            }}
            fullWidth
          >
            {whatsappMenuItem.title}
          </Button>
        </Stack>
      );
    }
  }

  useEffect(() => {
    // loadServicesList();
  }, []);

  return (
    <>
      <AppShell
        header={{
          height: {
            base: 72,
            md: 96,
          },
        }}
        navbar={{
          width: 300,
          breakpoint: "sm",
          collapsed: { desktop: true, mobile: !opened },
        }}
        padding="md"
      >
        <AppShell.Header>
          <Container
            fluid
            px={0}
            style={{
              background: theme.white,
              boxShadow: `0 2px 16px 0 ${theme.colors.gray[1]}`,
              borderBottom: `1.5px solid ${theme.colors.gray[2]}`,
              position: "sticky",
              top: 0,
              zIndex: 100,
            }}
          >
            <Group justify="space-between" align="center" h="100%" px={16}>
              <Group gap={16} align="center">
                <Stack
                  gap={0}
                  role="link"
                  tabIndex={0}
                  aria-label="Go to homepage"
                  style={{
                    lineHeight: 0,
                    marginLeft: 10,
                    cursor: "pointer",
                  }}
                  onClick={() => {
                    close();
                    navigate("/");
                  }}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      close();
                      navigate("/");
                    }
                  }}
                >
                  <SubjectBuddyLogo size={60} />
                </Stack>
              </Group>
              <Group visibleFrom="md">
                {createNavigationItems({ forDesktop: true })}
              </Group>
              <Burger
                opened={opened}
                onClick={toggle}
                hiddenFrom="md"
                size="md"
                color={theme.colors.green[7]}
                aria-label="Open navigation menu"
                style={{ marginLeft: 8 }}
              />
            </Group>
            <Drawer
              opened={opened}
              onClose={close}
              padding="md"
              size="md"
              title={null}
              hiddenFrom="md"
              overlayProps={{ opacity: 0.6, blur: 2 }}
              styles={{ body: { paddingTop: 24 } }}
            >
              {createNavigationItems({ forDesktop: false })}
            </Drawer>
          </Container>
        </AppShell.Header>
        <AppShell.Main px={0} pb={0} style={{ position: "relative" }}>
          <Outlet />

          <Button
            radius={"md"}
            size="md"
            component="a"
            href={whatsappUrl}
            target="_blank"
            rel="noreferrer"
            style={{
              position: "fixed",
              bottom: "0%",
              right: "2%",
              borderBottomLeftRadius: 0,
              borderBottomRightRadius: 0,
            }}
            leftSection={<IconBrandWhatsapp />}
          >
            WhatsApp us
          </Button>
          <Footer />
        </AppShell.Main>
      </AppShell>
    </>
  );
}

export default Layout;
